import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import Card from "../../../components/UI/card/Card";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { getUserId } from "../../../services/auth.service";
import { createNewEmployee } from "../../../services/employee.service";
import { DISTRIBUTOR_ROLE, EMPLOYEES_ROLES } from "../../../constants";
import { IDistributor } from "../../../interfaces/distributor.interface";
import { ROUTES } from "../../../constants/routes";
import { getApi } from "../../../services/http.service";
import {
  createNewDistributor,
  updateDistributorById,
} from "../../../services/distributor.service";
import VisibilityBox from "../../../components/common/VisibilityBox";

const validationSchema = Yup.object({
  firmName: Yup.string().required("Firm name is required"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be 10 digits"),
  address: Yup.object({
    street: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().default("India"),
    pincode: Yup.string().required("Pincode is required"),
  }),
  role: Yup.string().oneOf(DISTRIBUTOR_ROLE).required("Role is required"),
  accountNumber: Yup.string().optional(),
  adhaarCard: Yup.string().optional(),
  panCardNumber: Yup.string().optional(),
  associatedCompany: Yup.array().of(Yup.string()).optional(),
});

const DistributorForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const [displayImage, setDisplayImage] = useState<any>(null);
  const navigate = useNavigate();
  const params = useParams();
  const [distributor, setDistributor] = useState<IDistributor>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchDistributor();
  }, []);

  const fetchDistributor = async () => {
    try {
      const result = await getApi(ROUTES.DISTRIBUTOR_BY_ID + params?.id);
      setDistributor(result?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="w-100">
      <div className="row justify-content-center">
        <div className="col-lg-12 col-md-12 col-sm-12 w-100">
          <Card>
            <div className="product__edit p-4 w-100">
              <div className="d-flex">
                <Link className="nav-link" to={"/distributors"}>
                  <Icon
                    icon="material-symbols:arrow-back-rounded"
                    fontSize={25}
                  />
                </Link>
                <h4 className="ms-2">Distributor</h4>
              </div>

              <div className=" mb-2">
                <label htmlFor="pic" className="upload-icon mb-2">
                  <Icon
                    icon="akar-icons:cloud-upload"
                    style={{ fontSize: "40px", color: "#007bff" }}
                  />
                </label>
                <div className="d-none">
                  <input
                    className="form-control-file"
                    type="file"
                    id="pic"
                    name="pic"
                    onChange={(ev: any) =>
                      setDisplayImage(ev.currentTarget.files[0])
                    }
                    accept="image/png, image/jpeg"
                  />
                </div>
                <img
                  className="img-fluid rounded mt-3"
                  src={
                    displayImage
                      ? URL.createObjectURL(displayImage)
                      : "/local/default.png"
                  }
                  alt="profile pic"
                  style={{ maxWidth: "200px" }}
                />
              </div>

              {/* Formik Form */}
              <Formik
                initialValues={{
                  firmName: distributor?.firmName || "",
                  email: distributor?.email || "",
                  phone: distributor?.phone || "",
                  password: "",
                  address: distributor?.address || {
                    street: "",
                    city: "",
                    state: "",
                    country: "India",
                    pincode: "",
                  },
                  directorName: distributor?.directorName || "",
                  typeofFirm: distributor?.typeofFirm || "",
                  clientCode: distributor?.clientCode || "",
                  associatedCompanies: distributor?.associatedCompanies || [],
                  role: distributor?.role || DISTRIBUTOR_ROLE[0], // Default to first role
                }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={async (
                  values,
                  { resetForm }: FormikHelpers<IDistributor>
                ) => {
                  console.log(values);
                  if (params?.id) {
                    const payload = {
                      ...values,
                    };
                    const result = await updateDistributorById(
                      params?.id,
                      payload
                    );
                    if (result) {
                      resetForm();
                      setDisplayImage(null);
                      navigate("/distributors");
                    }
                  } else {
                    const payload = {
                      ...values,
                      createdBy: getUserId(),
                    };
                    const result = await createNewDistributor(payload);
                    if (result) {
                      resetForm();
                      setDisplayImage(null);
                      navigate("/distributors");
                    }
                  }
                }}
              >
                {({ errors, touched, values, setFieldValue }) => (
                  <Form>
                    {/* Full Name */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="firmName"
                        name="firmName"
                        type="text"
                        placeholder="Firm Name"
                        className={`form-control ${
                          errors.firmName && touched.firmName
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="firmName"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Email */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Email"
                        className={`form-control ${
                          errors.email && touched.email ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Phone */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="phone"
                        name="phone"
                        type="text"
                        placeholder="Phone Number"
                        className={`form-control ${
                          errors.phone && touched.phone ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Password */}
                    <VisibilityBox show={params?.id !== undefined}>
                      <div className="form-group p-0 mb-3">
                        <Field
                          id="password"
                          name="password"
                          type="password"
                          placeholder="Password"
                          className={`form-control ${
                            errors.password && touched.password
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </VisibilityBox>

                    {/* Address Fields */}
                    <h5>Address</h5>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.street"
                        name="address.street"
                        type="text"
                        placeholder="Street"
                        className={`form-control ${
                          errors.address?.street && touched.address?.street
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.street"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.city"
                        name="address.city"
                        type="text"
                        placeholder="City"
                        className={`form-control ${
                          errors.address?.city && touched.address?.city
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.city"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.state"
                        name="address.state"
                        type="text"
                        placeholder="State"
                        className={`form-control ${
                          errors.address?.state && touched.address?.state
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.state"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.pincode"
                        name="address.pincode"
                        type="text"
                        placeholder="Pincode"
                        className={`form-control ${
                          errors.address?.pincode && touched.address?.pincode
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.pincode"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Account Number */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="accountNumber"
                        name="accountNumber"
                        type="text"
                        placeholder="Account Number"
                        className={`form-control ${
                          errors.accountNumber && touched.accountNumber
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="accountNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Aadhar Card */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="adhaarCard"
                        name="adhaarCard"
                        type="text"
                        placeholder="Aadhar Card"
                        className={`form-control ${
                          errors.adhaarCard && touched.adhaarCard
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="adhaarCard"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Pan Card */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="panCardNumber"
                        name="panCardNumber"
                        type="text"
                        placeholder="Pan Card Number"
                        className={`form-control ${
                          errors.panCardNumber && touched.panCardNumber
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="panCardNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Role */}
                    <div className="form-group p-0 mb-3">
                      <Field as="select" name="role" className="form-control">
                        {DISTRIBUTOR_ROLE.map((role, index) => (
                          <option key={index} value={role}>
                            {role}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="role"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button type="submit" className="btn btn-primary px-4">
                        Submit
                      </button>
                      <Link to="/employees">
                        <button className="btn btn-secondary px-4">
                          {t("cancel")}
                        </button>
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DistributorForm;
